import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { http } from '@/api/http';
import { selectData, type SuccessResponseDTO } from '@/api/envelope';

const BASE = '/admin/api/moderation/lab';

export type LabFixture = {
  id: number;
  caseId: number;
  caption?: string | null;
  mediaS3Key?: string | null;
  mediaContentType?: string | null;
  mediaFileType?: string | null;
  sortOrder?: number;
  createdAt?: string;
  previewUrl?: string | null;
};

export type LabCase = {
  id: number;
  name: string;
  description?: string | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  fixtureCount?: number;
  fixtures?: LabFixture[];
};

export type LabRunResult = {
  id: number;
  runId: number;
  fixtureId: number;
  engine: 'gpu' | 'cloud' | string;
  status: string;
  durationMs?: number | null;
  resultJson?: Record<string, unknown> | null;
  errorMessage?: string | null;
  createdAt?: string;
};

export type LabRun = {
  id: number;
  caseId: number;
  status: string;
  triggeredBy?: string | null;
  startedAt?: string;
  finishedAt?: string | null;
  errorMessage?: string | null;
  results?: LabRunResult[];
};

export const labKeys = {
  cases: ['moderation', 'lab', 'cases'] as const,
  case: (id: string | number) => ['moderation', 'lab', 'cases', String(id)] as const,
  runs: (caseId: string | number) =>
    ['moderation', 'lab', 'cases', String(caseId), 'runs'] as const,
  run: (runId: string | number) => ['moderation', 'lab', 'runs', String(runId)] as const,
};

async function unwrap<T>(promise: Promise<{ data: SuccessResponseDTO<T> }>): Promise<T> {
  const res = await promise;
  return selectData(res.data) as T;
}

export function useLabCases() {
  return useQuery({
    queryKey: labKeys.cases,
    queryFn: () => unwrap(http.get<SuccessResponseDTO<LabCase[]>>(`${BASE}/cases`)),
  });
}

export function useLabCase(caseId: string | undefined) {
  return useQuery({
    queryKey: labKeys.case(caseId ?? ''),
    enabled: Boolean(caseId),
    queryFn: () => unwrap(http.get<SuccessResponseDTO<LabCase>>(`${BASE}/cases/${caseId}`)),
  });
}

export function useCreateLabCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      unwrap(http.post<SuccessResponseDTO<LabCase>>(`${BASE}/cases`, body)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: labKeys.cases }),
  });
}

export function useDeleteLabCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseId: number) =>
      unwrap(http.delete<SuccessResponseDTO<null>>(`${BASE}/cases/${caseId}`)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: labKeys.cases }),
  });
}

export function useUpdateLabCase(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      unwrap(http.put<SuccessResponseDTO<LabCase>>(`${BASE}/cases/${caseId}`, body)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: labKeys.case(caseId) });
      void qc.invalidateQueries({ queryKey: labKeys.cases });
    },
  });
}

export function useAddLabFixture(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      caption?: string;
      mediaS3Key?: string;
      mediaContentType?: string;
      sortOrder?: number;
    }) =>
      unwrap(
        http.post<SuccessResponseDTO<LabFixture>>(`${BASE}/cases/${caseId}/fixtures`, body),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: labKeys.case(caseId) });
      void qc.invalidateQueries({ queryKey: labKeys.cases });
    },
  });
}

export function useDeleteLabFixture(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fixtureId: number) =>
      unwrap(
        http.delete<SuccessResponseDTO<null>>(
          `${BASE}/cases/${caseId}/fixtures/${fixtureId}`,
        ),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: labKeys.case(caseId) });
      void qc.invalidateQueries({ queryKey: labKeys.cases });
    },
  });
}

export async function uploadLabMedia(file: File): Promise<{ s3Key: string; contentType: string }> {
  const { prepareLabUploadMedia } = await import('./lib/prepareLabUploadMedia');
  const prepared = await prepareLabUploadMedia(file);
  const contentType = prepared.type || 'application/octet-stream';
  const params = new URLSearchParams({
    fileName: prepared.name,
    contentType,
  });
  const urlRes = await http.post<SuccessResponseDTO<{ url: string; s3Key: string }>>(
    `${BASE}/upload-url?${params.toString()}`,
  );
  const data = selectData(urlRes.data);
  if (!data?.url || !data.s3Key) {
    throw new Error('Failed to get upload URL');
  }
  await axios.put(data.url, prepared, {
    headers: { 'Content-Type': contentType },
  });
  return { s3Key: data.s3Key, contentType };
}

export function useTriggerLabRun(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(http.post<SuccessResponseDTO<LabRun>>(`${BASE}/cases/${caseId}/runs`)),
    onSuccess: (run) => {
      void qc.invalidateQueries({ queryKey: labKeys.runs(caseId) });
      if (run?.id != null) {
        void qc.invalidateQueries({ queryKey: labKeys.run(run.id) });
      }
    },
  });
}

export function useLabRuns(caseId: string | undefined) {
  return useQuery({
    queryKey: labKeys.runs(caseId ?? ''),
    enabled: Boolean(caseId),
    queryFn: () =>
      unwrap(http.get<SuccessResponseDTO<LabRun[]>>(`${BASE}/cases/${caseId}/runs`)),
  });
}

/** Deep GPU + Cloud compare can take minutes; refresh until the run records a finish. */
const LAB_RUN_POLL_MS = 3000;

const LAB_RUN_TERMINAL_STATUSES = new Set([
  'completed',
  'complete',
  'succeeded',
  'success',
  'failed',
  'failure',
  'error',
  'cancelled',
  'canceled',
  'timeout',
]);

function isLabRunInFlight(run: LabRun | undefined): boolean {
  if (!run || run.finishedAt) return false;
  return !LAB_RUN_TERMINAL_STATUSES.has(run.status.trim().toLowerCase());
}

export function useLabRun(runId: string | undefined) {
  return useQuery({
    queryKey: labKeys.run(runId ?? ''),
    enabled: Boolean(runId),
    queryFn: () => unwrap(http.get<SuccessResponseDTO<LabRun>>(`${BASE}/runs/${runId}`)),
    refetchInterval: (query) => (isLabRunInFlight(query.state.data) ? LAB_RUN_POLL_MS : false),
  });
}
